import { useEffect, useRef, useState } from "react";
import { Input } from "@/ui/input";
import { Spinner } from "@/ui/spinner";
import { ICONES, Icone } from "../visual.jsx";

/**
 * Um texto que vira campo ao ser tocado.
 *
 * O mesmo contrato da ficha: clique abre, Enter grava, Esc desiste, sair do
 * campo grava. Nome vazio não grava — registro sem nome não existe, e quem
 * apagou tudo e clicou fora recebe o nome de volta em vez de um caso anônimo.
 *
 * Arquivo próprio desde 27/08: ele morava dentro de `CabecalhoDoPainel.jsx` e
 * agora é o cabeçalho da CONVERSA que edita o nome e o telefone do cliente —
 * onde o nome está escrito grande e onde a pessoa já está olhando. Peça usada em
 * duas telas sobe para a camada da casa; copiada em duas, envelhece em uma.
 *
 * Os ESTADOS, todos desenhados (17/10 — era um retângulo de borda fina que
 * mudava a fonte ao abrir e sumia sem recibo):
 *
 *   - **leitura**: texto puro, e o fundo acende no hover e no foco. Sem lápis:
 *     ele ocupava 19px até invisível, e é o nome e o telefone que precisam
 *     deles na faixa apertada;
 *   - **editando**: o campo tem a caixa e a fonte do texto e a LARGURA dele —
 *     abrir só acende a borda — e leva ✓ e ✕ logo depois, porque no dedo não há Enter
 *     nem Esc. Os dois seguram o `mousedown`: sem isso o blur gravaria antes do
 *     ✕ desistir;
 *   - **gravando**: o valor novo já aparece, esmaecido, com a roda — a ida ao
 *     servidor leva um segundo, e o nome antigo voltando nesse meio parecia que
 *     não tinha gravado;
 *   - **gravou**: o ✓ verde por um instante, o recibo na própria linha;
 *   - **erro**: o campo reabre com o que a pessoa digitou e a borda vermelha. O
 *     motivo já vem no recado; perder o texto é que não pode.
 *
 * `aoGravar` que devolve promessa entra nesses estados; `null`/`false` é erro
 * (é o que `tentar()` devolve quando falha).
 */
/* O controle da ponta não pode roubar o foco: o blur do campo gravaria antes
   de o clique chegar. */
const segurar = (e) => e.preventDefault();

export default function TextoEditavel({ valor, exibido, rotulo, convite, classe = "", mascara, aoGravar }) {
  /* `mascara` formata enquanto se digita (o telefone); sem ela o texto é livre. */
  const vestir = (v) => (mascara ? mascara(v) : v);
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState(valor);
  /* null | { fase: "gravando" | "gravou" | "erro", valor } */
  const [envio, setEnvio] = useState(null);
  const campo = useRef(null);

  useEffect(() => {
    if (!editando) return;
    campo.current?.focus();
    campo.current?.select();
  }, [editando]);

  useEffect(() => {
    if (envio?.fase !== "gravou") return;
    const t = setTimeout(() => setEnvio(null), 1400);
    return () => clearTimeout(t);
  }, [envio]);

  const gravar = async (bruto) => {
    const novo = String(bruto ?? "").trim();
    setEditando(false);
    if (novo === String(vestir(valor) ?? "").trim()) return;
    const r = aoGravar(novo);
    if (!r || typeof r.then !== "function") return;
    setEnvio({ fase: "gravando", valor: novo });
    const ok = await r;
    if (ok === null || ok === false) {
      setRascunho(novo);
      setEnvio({ fase: "erro", valor: novo });
      setEditando(true);
    } else setEnvio({ fase: "gravou", valor: novo });
  };

  const desistir = () => {
    setEditando(false);
    setEnvio(null);
  };

  if (editando) {
    const erro = envio?.fase === "erro";
    /* O campo mede o TEXTO, não a faixa: um fantasma invisível com o mesmo
       texto, a mesma fonte e a mesma caixa dá a medida, e o campo se deita
       por cima (`absolute`: dentro de grade ou flex o `<input>` empurraria a
       largura para os 20 caracteres que todo campo tem de nascença). `field-sizing: content` errava por uma letra (o
       começo do telefone rolava para fora) e não existe no Firefox. A caixa é
       a do botão de leitura — `-ml-1.5`, `px-1.5`, `py-px`, borda de 1px —,
       então abrir não move uma letra: só a borda acende. */
    return (
      <span className={`-ml-1.5 flex max-w-full min-w-0 items-center gap-1 ${classe}`}>
        <span className="relative flex min-w-0">
          <span aria-hidden="true" className="invisible min-w-[4ch] border py-px pr-[calc(0.375rem+6px)] pl-1.5 whitespace-pre">
            {rascunho || convite || " "}
          </span>
          <Input
            ref={campo}
            value={rascunho}
            placeholder={convite}
            aria-label={rotulo}
            inputMode={mascara ? "tel" : undefined}
            aria-invalid={erro || undefined}
            onChange={(e) => setRascunho(vestir(e.target.value))}
            onBlur={(e) => gravar(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                desistir();
              }
              if (e.key === "Enter") {
                e.preventDefault();
                gravar(e.currentTarget.value);
              }
            }}
            className={`absolute inset-0 h-full w-full min-w-0 rounded-controle border bg-card px-1.5 py-px [font:inherit] text-[length:inherit] leading-[inherit] tracking-[inherit] text-foreground shadow-none selection:bg-terra/25 selection:text-foreground placeholder:text-tinta-fantasma placeholder:italic focus-visible:ring-3 md:text-[length:inherit] ${
              erro
                ? "border-destructive/60 focus-visible:border-destructive/70 focus-visible:ring-destructive/15"
                : "border-terra/40 focus-visible:border-terra/60 focus-visible:ring-terra/15"
            }`}
          />
        </span>
        <button
          type="button"
          aria-label={`Gravar ${rotulo.toLowerCase()}`}
          data-dica="Gravar (Enter)"
          onMouseDown={segurar}
          onClick={() => gravar(campo.current?.value)}
          className="alvo flex size-5 flex-none items-center justify-center rounded-full bg-terra text-sobre-terra transition-transform duration-[var(--t-toque)] active:scale-90"
        >
          <Icone de={ICONES.confere} tamanho={12} />
        </button>
        <button
          type="button"
          aria-label="Desistir"
          data-dica="Desistir (Esc)"
          onMouseDown={segurar}
          onClick={desistir}
          className="alvo flex size-5 flex-none items-center justify-center rounded-full text-tinta-suave transition-colors duration-[var(--t-toque)] hover:bg-foreground/8 hover:text-foreground"
        >
          <Icone de={ICONES.fechar} tamanho={12} />
        </button>
      </span>
    );
  }

  const gravando = envio?.fase === "gravando";
  const texto = gravando ? envio.valor : (exibido ?? valor ?? "");
  return (
    <button
      type="button"
      onClick={() => {
        setRascunho(vestir(valor));
        setEditando(true);
      }}
      aria-label={`${rotulo}: ${valor || "vazio"}. Editar`}
      /* A dica traz o VALOR: nome longo trunca na coluna de 300px, e a dica é o
         único lugar onde ele aparece inteiro sem abrir o campo. */
      data-dica={valor ? `${valor} — clique para editar` : `${rotulo} — clique para preencher`}
      aria-busy={gravando || undefined}
      /* `-ml` alinha o texto com o resto da coluna: o respiro existe para o
         retângulo do `hover`, não para deslocar a identidade. */
      className={`alvo -ml-1.5 flex w-fit max-w-full min-w-0 cursor-text items-center gap-1.5 rounded-controle border border-transparent bg-transparent px-1.5 py-px text-left transition-colors duration-[var(--t-toque)] hover:bg-foreground/6 focus-visible:bg-foreground/6 ${classe} ${valor || gravando ? "" : "text-tinta-fantasma italic"}`}
    >
      <span className={`min-w-0 truncate transition-opacity ${gravando ? "opacity-55" : ""}`}>{texto || convite || ""}</span>
      {gravando ? (
        <Spinner aria-label="Gravando" className="size-3.5 flex-none text-tinta-suave" />
      ) : envio?.fase === "gravou" ? (
        <span role="status" aria-label="Gravado" className="flex flex-none animate-in text-verde duration-200 zoom-in-50 fade-in">
          <Icone de={ICONES.confere} tamanho={14} />
        </span>
      ) : null}
    </button>
  );
}
